// ============================================================
// swingUtils.js
// Swing high/low detection, adaptive lookback, and ATR trailing
// stop — all designed for SolCheddar's dual candle format
// (historical OHLC + live mcapLevels footprint).
// ============================================================

import { forEachLevel } from './candleHelpers'

/**
 * Returns the high and low mcap for any candle type.
 */
export function getCandleHL(candle) {
  if (candle.isHistorical) {
    return { high: candle.high_mcap ?? 0, low: candle.low_mcap ?? 0 }
  }
  let high = -Infinity, low = Infinity
  forEachLevel(candle, (mcap, buy, sell) => {
    if (buy + sell === 0) return
    if (mcap > high) high = mcap
    if (mcap < low)  low  = mcap
  })
  return {
    high: high === -Infinity ? 0 : high,
    low:  low  ===  Infinity ? 0 : low,
  }
}

/**
 * Returns the volume-weighted close mcap for any candle type.
 */
function getCandleClose(candle) {
  if (candle.isHistorical) return candle.close_mcap ?? 0
  let vol = 0, wsum = 0
  forEachLevel(candle, (mcap, buy, sell) => {
    const v = buy + sell
    wsum += mcap * v
    vol  += v
  })
  const { high, low } = getCandleHL(candle)
  return vol > 0 ? wsum / vol : (high + low) / 2
}

/**
 * Returns net delta (buy volume − sell volume) for any candle type.
 */
export function getCandleDelta(candle) {
  if (candle.isHistorical) {
    return (candle.buy_volume ?? 0) - (candle.sell_volume ?? 0)
  }
  let buy = 0, sell = 0
  forEachLevel(candle, (_mcap, b, s) => {
    buy  += b
    sell += s
  })
  return buy - sell
}

/**
 * Computes ATR% and maps it to a pivot lookback window.
 * High volatility = small n (catch fast moves),
 * low volatility  = large n (filter noise).
 */
export function computeAdaptiveLookback(candles, atrPeriod = 14) {
  const len = candles.length
  if (len < atrPeriod + 1) return 3

  let trSum = 0
  for (let i = len - atrPeriod; i < len; i++) {
    const { high, low } = getCandleHL(candles[i])
    const prevClose = getCandleClose(candles[i - 1])
    trSum += Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose))
  }
  const atr = trSum / atrPeriod
  const refPrice = getCandleClose(candles[len - 1])
  const atrPct = refPrice > 0 ? (atr / refPrice) * 100 : 5

  if (atrPct > 20) return 2
  if (atrPct > 12) return 3
  if (atrPct > 6)  return 4
  if (atrPct > 3)  return 5
  return 7
}

/**
 * Detects swing highs and lows using an N-bar pivot algorithm.
 * Pass n from computeAdaptiveLookback for auto-tuning.
 *
 * Each pivot: { index, mcap, delta, exhaustion, confidence, candle }
 * Exhaustion = seller domination at high, buyer domination at low.
 */
export function detectSwings(candles, n = 3) {
  const len = candles.length
  const highs  = new Float64Array(len)
  const lows   = new Float64Array(len)
  const deltas = new Float64Array(len)

  for (let i = 0; i < len; i++) {
    const hl = getCandleHL(candles[i])
    highs[i]  = hl.high
    lows[i]   = hl.low
    deltas[i] = getCandleDelta(candles[i])
  }

  const swingHighs = []
  const swingLows  = []

  for (let i = n; i < len - n; i++) {
    let isHigh = true, isLow = true
    for (let j = i - n; j <= i + n; j++) {
      if (j === i) continue
      if (highs[j] >= highs[i]) isHigh = false
      if (lows[j]  <= lows[i])  isLow  = false
      if (!isHigh && !isLow) break
    }
    if (isHigh) {
      const delta = deltas[i]
      swingHighs.push({ index: i, mcap: highs[i], delta, exhaustion: delta < 0, confidence: delta < 0 ? 'high' : 'low', candle: candles[i] })
    }
    if (isLow) {
      const delta = deltas[i]
      swingLows.push({ index: i, mcap: lows[i], delta, exhaustion: delta > 0, confidence: delta > 0 ? 'high' : 'low', candle: candles[i] })
    }
  }

  return { swingHighs, swingLows }
}

/**
 * Derives BOS and ChoCH market structure events from pivot sequences.
 */
export function detectStructure({ swingHighs, swingLows }) {
  const events = []
  for (let i = 1; i < swingHighs.length; i++) {
    if (swingHighs[i].mcap > swingHighs[i - 1].mcap)
      events.push({ type: 'BOS_HIGH', index: swingHighs[i].index, mcap: swingHighs[i].mcap })
  }
  for (let i = 1; i < swingLows.length; i++) {
    if (swingLows[i].mcap < swingLows[i - 1].mcap)
      events.push({ type: 'BOS_LOW', index: swingLows[i].index, mcap: swingLows[i].mcap })
  }
  for (let i = 2; i < swingLows.length; i++) {
    if (swingLows[i - 1].mcap < swingLows[i - 2].mcap && swingLows[i].mcap > swingLows[i - 1].mcap)
      events.push({ type: 'CHOCH_BULL', index: swingLows[i].index, mcap: swingLows[i].mcap })
  }
  for (let i = 2; i < swingHighs.length; i++) {
    if (swingHighs[i - 1].mcap > swingHighs[i - 2].mcap && swingHighs[i].mcap < swingHighs[i - 1].mcap)
      events.push({ type: 'CHOCH_BEAR', index: swingHighs[i].index, mcap: swingHighs[i].mcap })
  }
  return events.sort((a, b) => a.index - b.index)
}

/**
 * ATR Trailing Stop — port of the Pine Script ATR Trailing Stoploss.
 *
 * Algorithm:
 *   1. Wilder ATR over atrPeriod bars
 *   2. stopLine[i] = high[i] - mult * atr[i]
 *   3. TS[i] = rolling highest of stopLine over hhvPeriod bars
 *   4. bull = close > TS (green line), bear = close < TS (red line)
 *
 * @returns {Array<{ts: number, bull: boolean} | null>}
 *   One entry per candle. null during warmup period.
 */
export function computeATRTrailingStop(candles, atrPeriod = 5, hhvPeriod = 10, mult = 2.5) {
  const len    = candles.length
  const warmup = atrPeriod + hhvPeriod

  const closes = new Float64Array(len)
  const highs  = new Float64Array(len)
  const lows   = new Float64Array(len)

  for (let i = 0; i < len; i++) {
    const hl = getCandleHL(candles[i])
    highs[i]  = hl.high
    lows[i]   = hl.low
    closes[i] = getCandleClose(candles[i])
  }

  // Wilder's smoothed ATR
  const tr  = new Float64Array(len)
  const atr = new Float64Array(len)
  tr[0] = highs[0] - lows[0]
  for (let i = 1; i < len; i++) {
    tr[i] = Math.max(
      highs[i] - lows[i],
      Math.abs(highs[i] - closes[i - 1]),
      Math.abs(lows[i]  - closes[i - 1]),
    )
  }
  let sum = 0
  for (let i = 0; i < atrPeriod; i++) sum += tr[i]
  atr[atrPeriod - 1] = sum / atrPeriod
  for (let i = atrPeriod; i < len; i++) {
    atr[i] = (atr[i - 1] * (atrPeriod - 1) + tr[i]) / atrPeriod
  }

  // stopLine = high - mult * ATR
  const stopLine = new Float64Array(len)
  for (let i = atrPeriod - 1; i < len; i++) {
    stopLine[i] = highs[i] - mult * atr[i]
  }

  // Rolling highest of stopLine over hhvPeriod → trailing stop value
  const result = new Array(len).fill(null)
  for (let i = warmup - 1; i < len; i++) {
    let maxVal = -Infinity
    for (let j = Math.max(0, i - hhvPeriod + 1); j <= i; j++) {
      if (stopLine[j] > maxVal) maxVal = stopLine[j]
    }
    result[i] = { ts: maxVal, bull: closes[i] > maxVal }
  }

  return result
}

/**
 * Incremental ATR Trailing Stop — identical output to computeATRTrailingStop,
 * but O(1) per tick instead of O(candles × levels).
 *
 * Closed candles are immutable, so their TR/ATR/stopLine/result never change
 * once computed. Only the LIVE candle (last index) evolves intra-bar. This
 * function caches the full per-index arrays and, between structural changes,
 * recomputes ONLY the last index — so the trailing stop still ratchets on
 * every tick (the bot reads the live value) at constant cost.
 *
 * @param candles  the candle array
 * @param version  structuralVersion (bumps on close/trim/rebuild/switch and on
 *                 a late trade landing in a closed candle) — when it changes we
 *                 rebuild the whole cache; otherwise only the live bar.
 * @param cache    a persistent object (e.g. a useRef's .current) that this
 *                 function reads and mutates: { ver, base }.
 * @returns the same shape as computeATRTrailingStop.
 */
export function computeATRTrailingStopLive(candles, version, cache, atrPeriod = 5, hhvPeriod = 10, mult = 2.5) {
  const len    = candles.length
  const warmup = atrPeriod + hhvPeriod
  if (len < warmup + 1) {
    cache.ver = version; cache.base = null
    return new Array(len).fill(null)
  }

  const base = cache.base
  const structural = cache.ver !== version || !base || base.len !== len

  if (structural) {
    // ── Full rebuild (infrequent): compute every index, cache the arrays so
    //    the live bar can be re-derived cheaply until the next structural change.
    const closes = new Float64Array(len)
    const highs  = new Float64Array(len)
    const lows   = new Float64Array(len)
    for (let i = 0; i < len; i++) {
      const hl = getCandleHL(candles[i])
      highs[i] = hl.high; lows[i] = hl.low; closes[i] = getCandleClose(candles[i])
    }
    const tr  = new Float64Array(len)
    const atr = new Float64Array(len)
    tr[0] = highs[0] - lows[0]
    for (let i = 1; i < len; i++) {
      tr[i] = Math.max(highs[i] - lows[i], Math.abs(highs[i] - closes[i - 1]), Math.abs(lows[i] - closes[i - 1]))
    }
    let sum = 0
    for (let i = 0; i < atrPeriod; i++) sum += tr[i]
    atr[atrPeriod - 1] = sum / atrPeriod
    for (let i = atrPeriod; i < len; i++) atr[i] = (atr[i - 1] * (atrPeriod - 1) + tr[i]) / atrPeriod
    const stopLine = new Float64Array(len)
    for (let i = atrPeriod - 1; i < len; i++) stopLine[i] = highs[i] - mult * atr[i]
    const result = new Array(len).fill(null)
    for (let i = warmup - 1; i < len; i++) {
      let mx = -Infinity
      for (let j = Math.max(0, i - hhvPeriod + 1); j <= i; j++) if (stopLine[j] > mx) mx = stopLine[j]
      result[i] = { ts: mx, bull: closes[i] > mx }
    }
    cache.ver = version
    cache.base = { len, closes, highs, lows, tr, atr, stopLine, result }
    return result
  }

  // ── Incremental: structural set unchanged; only the live candle (last
  //    index) can have moved. Recompute that one index in place. Every value
  //    it depends on from index len-2 and back is a closed candle → immutable.
  const b = base
  const i = len - 1
  const hl = getCandleHL(candles[i])
  b.highs[i] = hl.high; b.lows[i] = hl.low; b.closes[i] = getCandleClose(candles[i])
  b.tr[i]  = Math.max(b.highs[i] - b.lows[i], Math.abs(b.highs[i] - b.closes[i - 1]), Math.abs(b.lows[i] - b.closes[i - 1]))
  b.atr[i] = (b.atr[i - 1] * (atrPeriod - 1) + b.tr[i]) / atrPeriod
  b.stopLine[i] = b.highs[i] - mult * b.atr[i]
  let mx = -Infinity
  for (let j = Math.max(0, i - hhvPeriod + 1); j <= i; j++) if (b.stopLine[j] > mx) mx = b.stopLine[j]
  b.result[i] = { ts: mx, bull: b.closes[i] > mx }
  return b.result
}
